import {Component, computed, effect, inject, OnInit, signal, Signal} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {DisableRuleSupplierActionEnum, IDisableStockItemRule, IOnHandDisableRule, SettingsReferenceEnum, TLineColour} from '../../models';
import {
  selectSelectedSetting,
  selectSelectedStoreDisableItemRule,
  selectSelectedStoreLineColours,
} from '../../store/setting.selectors';
import {IUser} from '../../../user-access/models';
import {selectUser} from '../../../user-access/user-store/user.selectors';
import {IStore} from '../../../../shared/shared-models/store/store';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {getFieldErrorString} from '../../../../shared/utils/form/form.utils';
import {settingsSetSelected, settingStockItemEnableRules} from '../../store/setting.actions';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {ICrudOptions, IUserAccessFeatureStockRules} from '../../../user-access/models/user-access-features/access';
import {
  selectUserAccessFeatureStockRulesForSelectedStore,
} from '../../../user-access/user-access-store/user-access.selectors';
import {
  decodeOnHoldCode,
  getAllOnHoldOptions,
} from '../../../../shared/shared-module-ui/utils/on-hold-code/on-hold-code.utils';
import {getStoreSuppliersByUserSelectedStore} from '../../../core/store-shared/shared.actions';
import {selectSuppliersByUserSelectedStore} from '../../../core/store-shared/shared.selectors';
import {ISupplier} from '../../../../shared/shared-models/stock/suppliers';

@Component({
  selector: 'app-feature-stock-rule-disable',
  templateUrl: './feature-stock-rule-disable.component.html',
  styleUrl: './feature-stock-rule-disable.component.scss',
  standalone: false,
})
export class FeatureStockRuleDisableComponent implements OnInit {

  //#region INJECTORS
  private formBuilder = inject(FormBuilder);
  private _store = inject(Store);
  private modalUtils = inject(SharedModalUtils);
  //#endregion

  //#region PROPERTIES
  formDisableItemRule: FormGroup;

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;
  protected readonly supplierActionEnum = DisableRuleSupplierActionEnum;

  private readonly _suppliers: Signal<Record<string, ISupplier>> = this._store.selectSignal(selectSuppliersByUserSelectedStore);

  private readonly _settingChanged: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedSetting);
  private readonly _disableItemRule: Signal<IDisableStockItemRule> = this._store.selectSignal(selectSelectedStoreDisableItemRule);
  private readonly _lineColours: Signal<TLineColour> = this._store.selectSignal(selectSelectedStoreLineColours);
  private readonly _user: Signal<IUser> = this._store.selectSignal(selectUser);
  private readonly _selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  private readonly _uaStockRules: Signal<IUserAccessFeatureStockRules> = this._store.selectSignal(selectUserAccessFeatureStockRulesForSelectedStore);

  protected onHandNotZeroOnHoldCodeSelected = signal<string[]>([]);
  protected onHandZeroOnHoldCodeSelected = signal<string[]>([]);
  protected onHandNotZeroSupplierActionSelected = signal<string[]>([]);
  protected onHandZeroSupplierActionSelected = signal<string[]>([]);
  protected onHandNotZeroRegularSuppCodeSelected = signal<string[]>([]);
  protected onHandZeroRegularSuppCodeSelected = signal<string[]>([]);

  protected readonly onHandNotZeroSupplierActionOptions = computed<Record<string, ISelectOption>>(() =>
    this.buildSupplierActionOptions(this.onHandNotZeroSupplierActionSelected()[0]),
  );

  protected readonly onHandZeroSupplierActionOptions = computed<Record<string, ISelectOption>>(() =>
    this.buildSupplierActionOptions(this.onHandZeroSupplierActionSelected()[0]),
  );

  protected readonly onHandNotZeroSupplierOptions = computed<Record<string, ISelectOption>>(() =>
    this.buildSupplierOptions(this.onHandNotZeroRegularSuppCodeSelected()),
  );

  protected readonly onHandZeroSupplierOptions = computed<Record<string, ISelectOption>>(() =>
    this.buildSupplierOptions(this.onHandZeroRegularSuppCodeSelected()),
  );

  //#endregion

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const userAccess = this._uaStockRules();
      const disableItemRule = this._disableItemRule();
      this.initializeForms(userAccess, disableItemRule);
    });
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });
  }

  //#endregion

  //#region ACCESSOR - DISABLE ITEM RULE SETTINGS

  get hasDisableItemRuleBeenEdited(): boolean {
    return this.formDisableItemRule?.dirty ?? false;
  }

  get hasDisableItemRuleErrors(): {
    errors: ValidationErrors; error: string; field: string;
  }[] {
    const errors: { errors: ValidationErrors; error: string; field: string; }[] = [];
    const fields = [
      'onHandNotZeroDescPrefix',
      'onHandNotZeroDescSuffix',
      'onHandNotZeroLineColour',
      'onHandNotZeroOnHoldCode',
      'onHandNotZeroSupplierAction',
      'onHandNotZeroRegularSuppCode',
      'onHandZeroDescPrefix',
      'onHandZeroDescSuffix',
      'onHandZeroLineColour',
      'onHandZeroOnHoldCode',
      'onHandZeroSupplierAction',
      'onHandZeroRegularSuppCode',
    ];

    for (const field of fields) {
      const fieldErrors = this.formDisableItemRule?.controls[field]?.errors;
      if (fieldErrors) {
        errors.push({
          errors: fieldErrors,
          error: getFieldErrorString(fieldErrors, field),
          field,
        });
      }
    }

    return errors;
  }

  get isDisableItemRuleSubmitDisabled(): boolean {
    return (this.canDisableItemRuleBeEdited === false) || (this.canDisableItemRuleBeEdited === true && this.hasDisableItemRuleBeenEdited === false) || (this.formDisableItemRule?.invalid ?? true);
  }

  get isSaveAllDisabled(): boolean {
    return this.isDisableItemRuleSubmitDisabled;
  }

  get canDisableItemRuleBeEdited(): boolean {
    const access = this.stockManagerUserAccess?.disable;
    return access?.canEdit ?? false;
  }

  get canDisableItemRuleBeSubmitted(): boolean {
    const access = this.stockManagerUserAccess?.disable;
    return access?.canSubmit ?? false;
  }

  get canDisableItemRuleBeViewed(): boolean {
    const access = this.stockManagerUserAccess?.disable;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - USER SETTINGS + USER ACCESS + STORE
  get selectedStore(): IStore {
    return this._selectedStore();
  }

  get userSettings(): IUser {
    return this._user();
  }

  get stockManagerUserAccess(): IUserAccessFeatureStockRules {
    return this._uaStockRules();
  }

  //#endregion

  //#region ACCESSOR - GLOBALS
  get haveAnyEditsBeenMade(): boolean {
    return (this.formDisableItemRule?.dirty ?? false);
  }

  get disableItemRule(): IDisableStockItemRule {
    return this._disableItemRule();
  }

  //#endregion

  //#region LIFECYCLE HOOKS
  ngOnInit(): void {
    this.formDisableItemRule = this.formBuilder.group({});
    this._store.dispatch(getStoreSuppliersByUserSelectedStore({dispatch: true}));
  }

  //#endregion

  //#region FORM - CLICK HANDLERS
  onRevertForm(form: string): void {
    switch (form) {
      case 'all':
        this.revertDisableItemRuleDetails();
        break;
      case 'disableItemRule':
        this.revertDisableItemRuleDetails();
        break;
    }
  }

  onSubmitForm(form: string): void {
    if (this.isDisableItemRuleSubmitDisabled) {
      return;
    }

    let data: Partial<IDisableStockItemRule> = {...this.disableItemRule};
    switch (form) {
      case 'all':
        data = {
          ...data,
          ...this.generateDisableItemRulePatch(data),
        };
        break;
      case 'disableItemRule':
        if (!this.canDisableItemRuleBeSubmitted) {
          return;
        }
        data = {...data, ...this.generateDisableItemRulePatch(data)};
        break;
      default:
        return;
    }
    this._store.dispatch(settingStockItemEnableRules.setDisableStockItemRule({settings: data as IDisableStockItemRule}));
    this.formDisableItemRule.markAsPristine();
  }

  //#endregion

  //#region FORM - BUILD FORMS FROM ACCESS

  private initializeForms(access: IUserAccessFeatureStockRules, rule: IDisableStockItemRule): void {
    this.buildDisableItemRuleForm(access?.disable, rule);
  }

  private buildDisableItemRuleForm(access: ICrudOptions, rule: IDisableStockItemRule): void {
    if (access?.canView && this.formDisableItemRule.pristine) {
      const tempForm = this.formBuilder.group({});

      this.addFormControl(tempForm, 'onHandNotZeroDescPrefix', rule.onHandNotZero?.descPrefix ?? '', false, [Validators.maxLength(200)]);
      this.addFormControl(tempForm, 'onHandNotZeroDescSuffix', rule.onHandNotZero?.descSuffix ?? '', false, [Validators.maxLength(200)]);
      this.addFormControl(tempForm, 'onHandNotZeroLineColour', rule.onHandNotZero?.lineColour ?? 0, false, [Validators.min(0), Validators.max(99)]);
      this.addFormControl(tempForm, 'onHandNotZeroOnHoldCode', rule.onHandNotZero?.onHoldCode ?? 0, false, [Validators.min(0)]);
      this.addFormControl(
        tempForm,
        'onHandNotZeroSupplierAction',
        rule.onHandNotZero?.supplierAction ?? DisableRuleSupplierActionEnum.none,
        false,
      );
      this.addFormControl(
        tempForm,
        'onHandNotZeroRegularSuppCode',
        rule.onHandNotZero?.regularSuppCode ?? '',
        false,
        [this.requiredWhenSupplierActionIsSet('onHandNotZeroSupplierAction')],
      );

      this.addFormControl(tempForm, 'onHandZeroDescPrefix', rule.onHandZero?.descPrefix ?? '', false, [Validators.maxLength(200)]);
      this.addFormControl(tempForm, 'onHandZeroDescSuffix', rule.onHandZero?.descSuffix ?? '', false, [Validators.maxLength(200)]);
      this.addFormControl(tempForm, 'onHandZeroLineColour', rule.onHandZero?.lineColour ?? 0, false, [Validators.min(0), Validators.max(99)]);
      this.addFormControl(tempForm, 'onHandZeroOnHoldCode', rule.onHandZero?.onHoldCode ?? 0, false, [Validators.min(0)]);
      this.addFormControl(
        tempForm,
        'onHandZeroSupplierAction',
        rule.onHandZero?.supplierAction ?? DisableRuleSupplierActionEnum.none,
        false,
      );
      this.addFormControl(
        tempForm,
        'onHandZeroRegularSuppCode',
        rule.onHandZero?.regularSuppCode ?? '',
        false,
        [this.requiredWhenSupplierActionIsSet('onHandZeroSupplierAction')],
      );

      const onHandNotZeroDecodedCodes = decodeOnHoldCode(rule.onHandNotZero?.onHoldCode ?? 0, {returnCodes: true});
      const onHandNotZeroPrefixedCodes = onHandNotZeroDecodedCodes.map((code: string) => `ohc-${code}`);
      this.onHandNotZeroOnHoldCodeSelected.set(onHandNotZeroPrefixedCodes);

      const onHandZeroDecodedCodes = decodeOnHoldCode(rule.onHandZero?.onHoldCode ?? 0, {returnCodes: true});
      const onHandZeroPrefixedCodes = onHandZeroDecodedCodes.map((code: string) => `ohc-${code}`);
      this.onHandZeroOnHoldCodeSelected.set(onHandZeroPrefixedCodes);

      const onHandNotZeroSupplierAction = rule.onHandNotZero?.supplierAction ?? DisableRuleSupplierActionEnum.none;
      this.onHandNotZeroSupplierActionSelected.set([onHandNotZeroSupplierAction]);
      this.onHandNotZeroRegularSuppCodeSelected.set(
        rule.onHandNotZero?.regularSuppCode ? [`sup-${rule.onHandNotZero.regularSuppCode}`] : [],
      );

      const onHandZeroSupplierAction = rule.onHandZero?.supplierAction ?? DisableRuleSupplierActionEnum.none;
      this.onHandZeroSupplierActionSelected.set([onHandZeroSupplierAction]);
      this.onHandZeroRegularSuppCodeSelected.set(
        rule.onHandZero?.regularSuppCode ? [`sup-${rule.onHandZero.regularSuppCode}`] : [],
      );

      tempForm.markAsPristine();
      this.formDisableItemRule = tempForm;
    }
  }

  private addFormControl<T = unknown>(form: FormGroup, controlName: string, value: T, disabled: boolean, validators: ValidatorFn[] = []): void {
    form.addControl(controlName, this.formBuilder.control({value, disabled}, validators));
  }

  private requiredWhenSupplierActionIsSet(supplierActionControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const supplierAction = control.parent?.get(supplierActionControlName)?.value as DisableRuleSupplierActionEnum;
      if (supplierAction === DisableRuleSupplierActionEnum.set && !String(control.value ?? '').trim()) {
        return {required: true};
      }
      return null;
    };
  }

  //#endregion

  //#region FORM - ON CLICK

  onLineColourChange(selected: { [key: string]: ISelectOption }, property: string): void {
    const selection = Object.keys(selected).filter((option: string) => selected[option].selected);
    const selectedKey: string = selection?.length > 0 ? selection[0] : 'lc-0';
    const lineColourCode = parseInt(selectedKey.replace('lc-', ''), 10);
    this.formDisableItemRule.controls[property].setValue(lineColourCode);
    this.formDisableItemRule.markAsDirty();
  }

  onOnHoldCodeChange(selected: { [key: string]: ISelectOption }, property: string): void {
    const selectedKeys = Object.keys(selected).filter((key: string) => selected[key].selected);
    const selectedCodes = selectedKeys.map((key: string) => parseInt(key.replace('ohc-', ''), 10));
    const encodedValue = selectedCodes.reduce((sum: number, code: number) => sum + code, 0);

    if (property === 'onHandNotZeroOnHoldCode') {
      this.onHandNotZeroOnHoldCodeSelected.set(selectedKeys);
    } else if (property === 'onHandZeroOnHoldCode') {
      this.onHandZeroOnHoldCodeSelected.set(selectedKeys);
    }

    this.formDisableItemRule.controls[property].setValue(encodedValue);
    this.formDisableItemRule.markAsDirty();
  }

  onSupplierActionChange(selected: Record<string, ISelectOption>, property: string): void {
    const selectedKey = Object.keys(selected).find((key: string) => selected[key].selected) ?? DisableRuleSupplierActionEnum.none;
    const supplierAction = selectedKey as DisableRuleSupplierActionEnum;

    if (property === 'onHandNotZeroSupplierAction') {
      this.onHandNotZeroSupplierActionSelected.set([selectedKey]);
    } else if (property === 'onHandZeroSupplierAction') {
      this.onHandZeroSupplierActionSelected.set([selectedKey]);
    }

    this.formDisableItemRule.controls[property].setValue(supplierAction);
    this.formDisableItemRule.markAsDirty();

    const regularSuppCodeControlName = property === 'onHandNotZeroSupplierAction'
      ? 'onHandNotZeroRegularSuppCode'
      : 'onHandZeroRegularSuppCode';

    if (supplierAction !== DisableRuleSupplierActionEnum.set) {
      this.formDisableItemRule.controls[regularSuppCodeControlName].setValue('');
      if (property === 'onHandNotZeroSupplierAction') {
        this.onHandNotZeroRegularSuppCodeSelected.set([]);
      } else {
        this.onHandZeroRegularSuppCodeSelected.set([]);
      }
    }

    this.formDisableItemRule.controls[regularSuppCodeControlName].updateValueAndValidity();
  }

  onRegularSuppCodeChange(selected: Record<string, ISelectOption>, property: string): void {
    const selectedKey = Object.keys(selected).find((key: string) => selected[key].selected);
    const supplierCode = selectedKey ? selectedKey.replace('sup-', '') : '';

    if (property === 'onHandNotZeroRegularSuppCode') {
      this.onHandNotZeroRegularSuppCodeSelected.set(selectedKey ? [selectedKey] : []);
    } else if (property === 'onHandZeroRegularSuppCode') {
      this.onHandZeroRegularSuppCodeSelected.set(selectedKey ? [selectedKey] : []);
    }

    this.formDisableItemRule.controls[property].setValue(supplierCode);
    this.formDisableItemRule.markAsDirty();
  }

  //#endregion

  //#region FORM - REVERT FORM EDITS
  private revertDisableItemRuleDetails(): void {
    const rule = this.disableItemRule;

    this.formDisableItemRule.reset({
      onHandNotZeroDescPrefix: rule.onHandNotZero?.descPrefix ?? '',
      onHandNotZeroDescSuffix: rule.onHandNotZero?.descSuffix ?? '',
      onHandNotZeroLineColour: rule.onHandNotZero?.lineColour ?? 0,
      onHandNotZeroOnHoldCode: rule.onHandNotZero?.onHoldCode ?? 0,
      onHandNotZeroSupplierAction: rule.onHandNotZero?.supplierAction ?? DisableRuleSupplierActionEnum.none,
      onHandNotZeroRegularSuppCode: rule.onHandNotZero?.regularSuppCode ?? '',
      onHandZeroDescPrefix: rule.onHandZero?.descPrefix ?? '',
      onHandZeroDescSuffix: rule.onHandZero?.descSuffix ?? '',
      onHandZeroLineColour: rule.onHandZero?.lineColour ?? 0,
      onHandZeroOnHoldCode: rule.onHandZero?.onHoldCode ?? 0,
      onHandZeroSupplierAction: rule.onHandZero?.supplierAction ?? DisableRuleSupplierActionEnum.none,
      onHandZeroRegularSuppCode: rule.onHandZero?.regularSuppCode ?? '',
    });

    const onHandNotZeroDecodedCodes = decodeOnHoldCode(rule.onHandNotZero?.onHoldCode ?? 0, {returnCodes: true});
    const onHandNotZeroPrefixedCodes = onHandNotZeroDecodedCodes.map((code: string) => `ohc-${code}`);
    this.onHandNotZeroOnHoldCodeSelected.set(onHandNotZeroPrefixedCodes);

    const onHandZeroDecodedCodes = decodeOnHoldCode(rule.onHandZero?.onHoldCode ?? 0, {returnCodes: true});
    const onHandZeroPrefixedCodes = onHandZeroDecodedCodes.map((code: string) => `ohc-${code}`);
    this.onHandZeroOnHoldCodeSelected.set(onHandZeroPrefixedCodes);

    const onHandNotZeroSupplierAction = rule.onHandNotZero?.supplierAction ?? DisableRuleSupplierActionEnum.none;
    this.onHandNotZeroSupplierActionSelected.set([onHandNotZeroSupplierAction]);
    this.onHandNotZeroRegularSuppCodeSelected.set(
      rule.onHandNotZero?.regularSuppCode ? [`sup-${rule.onHandNotZero.regularSuppCode}`] : [],
    );

    const onHandZeroSupplierAction = rule.onHandZero?.supplierAction ?? DisableRuleSupplierActionEnum.none;
    this.onHandZeroSupplierActionSelected.set([onHandZeroSupplierAction]);
    this.onHandZeroRegularSuppCodeSelected.set(
      rule.onHandZero?.regularSuppCode ? [`sup-${rule.onHandZero.regularSuppCode}`] : [],
    );
  }

  //#endregion

  //#region FORM - GENERATE PATCH DATA

  private generateDisableItemRulePatch(data: Partial<IDisableStockItemRule>): Partial<IDisableStockItemRule> {
    const formValues = this.formDisableItemRule.getRawValue();

    const rulePatch: Partial<IDisableStockItemRule> = {
      onHandNotZero: this.buildOnHandDisableRulePatch(
        formValues?.onHandNotZeroDescPrefix,
        formValues?.onHandNotZeroDescSuffix,
        formValues?.onHandNotZeroLineColour,
        formValues?.onHandNotZeroOnHoldCode,
        formValues?.onHandNotZeroSupplierAction,
        formValues?.onHandNotZeroRegularSuppCode,
      ),
      onHandZero: this.buildOnHandDisableRulePatch(
        formValues?.onHandZeroDescPrefix,
        formValues?.onHandZeroDescSuffix,
        formValues?.onHandZeroLineColour,
        formValues?.onHandZeroOnHoldCode,
        formValues?.onHandZeroSupplierAction,
        formValues?.onHandZeroRegularSuppCode,
      ),
    };

    data = {...data, ...rulePatch};
    return data;
  }

  private buildOnHandDisableRulePatch(
    descPrefix: string,
    descSuffix: string,
    lineColour: number,
    onHoldCode: number,
    supplierAction: DisableRuleSupplierActionEnum,
    regularSuppCode: string,
  ): IOnHandDisableRule {
    const action: DisableRuleSupplierActionEnum = supplierAction ?? DisableRuleSupplierActionEnum.none;
    const rule: IOnHandDisableRule = {
      descPrefix: descPrefix?.trim() ?? '',
      descSuffix: descSuffix?.trim() ?? '',
      lineColour,
      onHoldCode,
    };

    if (action !== DisableRuleSupplierActionEnum.none) {
      rule.supplierAction = action;
    }

    if (action === DisableRuleSupplierActionEnum.set) {
      rule.regularSuppCode = regularSuppCode?.trim() ?? '';
    }

    return rule;
  }

  //#endregion

  //#region SELECT - OPTIONS

  getLineColourOptions(selectedCode: number | string | null | undefined): Record<string, ISelectOption> {
    const lineColours = this._lineColours();
    const selectedKey = `lc-${selectedCode ?? 0}`;

    const allLineColours = [
      {
        code: '0',
        description: 'No Line Colour',
        value: 'transparent',
      },
      ...Object.entries(lineColours).map(([key, value]) => ({
        code: key,
        description: value.description,
        value: value.value,
      })),
    ];

    const sortedLineColours = allLineColours.sort((a, b) =>
      a.description.localeCompare(b.description),
    );

    const options: Record<string, ISelectOption> = {};

    sortedLineColours.forEach((lineColour) => {
      const key = `lc-${lineColour.code}`;
      options[key] = {
        name: `${lineColour.description} (${lineColour.code})`,
        selected: key === selectedKey,
        disabled: false,
        colour: lineColour.value,
      };
    });

    return options;
  }

  getOnHoldCodeOptions(selectedKeys: string[]): Record<string, ISelectOption> {
    const onHoldOptions = getAllOnHoldOptions(false);
    const selected = selectedKeys ?? [];

    const sortedOptions = onHoldOptions
      .sort((a: { code: number; label: string }, b: { code: number; label: string }) =>
        a.label.localeCompare(b.label));

    const options: Record<string, ISelectOption> = {};

    sortedOptions.forEach((option: { code: number; label: string }) => {
      const key = `ohc-${option.code}`;
      options[key] = {
        name: option.label,
        selected: selected.includes(key),
        disabled: false,
      };
    });

    return options;
  }

  private buildSupplierActionOptions(selectedKey: string | undefined): Record<string, ISelectOption> {
    const selected = selectedKey ?? DisableRuleSupplierActionEnum.none;
    return {
      none: {
        name: 'No supplier change',
        selected: selected === DisableRuleSupplierActionEnum.none,
        disabled: false,
      },
      remove: {
        name: 'Remove regular supplier',
        selected: selected === DisableRuleSupplierActionEnum.remove,
        disabled: false,
      },
      set: {
        name: 'Set regular supplier',
        selected: selected === DisableRuleSupplierActionEnum.set,
        disabled: false,
      },
    };
  }

  private buildSupplierOptions(selectedKeys: string[]): Record<string, ISelectOption> {
    const suppliers = this._suppliers();
    const selected = selectedKeys ?? [];
    const options: Record<string, ISelectOption> = {};

    Object.entries(suppliers)
      .sort(([, a], [, b]) => (a.name ?? '').localeCompare(b.name ?? ''))
      .forEach(([supplierCode, supplier]) => {
        const key = `sup-${supplierCode}`;
        options[key] = {
          name: `${supplierCode} - ${supplier.name ?? ''}`,
          selected: selected.includes(key),
          disabled: false,
        };
      });

    return options;
  }

  //#endregion

  //#region MODAL - DISCARD UNSAVED CHANGES
  private async onShowModalUnsavedChanges(ref: SettingsReferenceEnum): Promise<void> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };
    await this.modalUtils.createModal(modalProps).then((result: IModalDismissResult) => {
      this._store.dispatch(settingsSetSelected.setSelectedReference({ref: SettingsReferenceEnum.accountUserProfile}));
      if (result.role === 'modal-accept') {
        this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
      }
    });
  }

  //#endregion

}
